import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { Button, Card, Select, Spinner } from "@nutrayan/ui";
import { formatCurrency, formatIndianDate } from "@nutrayan/utils";
import { apiClient } from "@/lib/api-client";

// Contract: GET /admin/api/v1/orders/{id} → OrderView; POST .../transition
// (TransitionOrderCommand), .../cancel, .../approve-return.

const STATUSES = [
  "PENDING_PAYMENT",
  "COD_PENDING_CONFIRM",
  "COD_VERIFIED",
  "CONFIRMED",
  "ALLOCATED",
  "READY_TO_SHIP",
  "SHIPPED",
  "DELIVERED",
  "COMPLETED",
  "PAYMENT_FAILED",
  "CANCELLED",
  "RETURN_REQUESTED",
  "RETURN_PENDING",
  "REFUNDED",
] as const;

interface OrderLineView {
  id: number;
  sku?: string;
  nameSnapshot?: string;
  quantity?: number;
  lineTotalPaise?: number;
}

interface OrderView {
  id: number;
  orderNumber?: string;
  status?: string;
  paymentStatus?: string;
  paymentMethod?: string;
  subtotalPaise?: number;
  discountPaise?: number;
  shippingPaise?: number;
  codFeePaise?: number;
  taxPaise?: number;
  grandTotalPaise?: number;
  couponCode?: string;
  deliveryAddress?: {
    name?: string;
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    phone?: string;
  };
  lines?: OrderLineView[];
  placedAt?: string;
  customerNote?: string;
  cancelReason?: string;
  /** Shipment tracking number, when a shipment exists (BLK-API-003). */
  awbNumber?: string;
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [target, setTarget] = useState<string>("CONFIRMED");

  const order = useQuery({
    queryKey: ["admin", "order", id],
    queryFn: () => apiClient.request<OrderView>(`/admin/api/v1/orders/${id}`),
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "order", id] });
    qc.invalidateQueries({ queryKey: ["admin", "orders"] });
  };
  const onError = (e: Error) => toast.error(e.message || "Action failed");

  const transition = useMutation({
    mutationFn: (targetStatus: string) =>
      apiClient.request<OrderView>(`/admin/api/v1/orders/${id}/transition`, {
        method: "POST",
        body: { targetStatus, reason: "Manual transition from admin" },
      }),
    onSuccess: () => {
      toast.success("Status updated");
      invalidate();
    },
    onError,
  });

  const cancel = useMutation({
    mutationFn: () =>
      apiClient.request<OrderView>(`/admin/api/v1/orders/${id}/cancel`, {
        method: "POST",
        body: { reason: "Cancelled by admin" },
      }),
    onSuccess: () => {
      toast.success("Order cancelled");
      invalidate();
    },
    onError,
  });

  const approveReturn = useMutation({
    mutationFn: () =>
      apiClient.request<OrderView>(`/admin/api/v1/orders/${id}/approve-return`, {
        method: "POST",
      }),
    onSuccess: () => {
      toast.success("Return approved — refund initiated");
      invalidate();
    },
    onError,
  });

  if (order.isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }
  if (order.isError || !order.data) {
    return (
      <Card>
        <p className="text-body text-terracotta">Couldn&apos;t load order #{id}.</p>
      </Card>
    );
  }

  const o = order.data;
  const addr = o.deliveryAddress;

  return (
    <div className="max-w-4xl space-y-6">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">
          Order {o.orderNumber ?? o.id}
        </h1>
        <p className="text-body text-ink">
          {o.placedAt ? `Placed ${formatIndianDate(o.placedAt)} · ` : ""}
          {(o.status ?? "").replaceAll("_", " ").toLowerCase()} · payment{" "}
          {(o.paymentStatus ?? "—").toLowerCase()} ({o.paymentMethod ?? "—"})
        </p>
        {o.awbNumber ? (
          <p className="text-body-sm text-ink mt-1">
            Tracking (AWB): <span className="font-mono">{o.awbNumber}</span>
          </p>
        ) : null}
      </header>

      <Card>
        <h2 className="font-display text-display-sm text-charcoal">Lines</h2>
        <table className="text-body-sm mt-3 w-full">
          <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
            <tr>
              <th className="py-2">Item</th>
              <th className="py-2">SKU</th>
              <th className="py-2 text-right">Qty</th>
              <th className="py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {(o.lines ?? []).map((l) => (
              <tr key={l.id} className="border-charcoal/5 border-b">
                <td className="text-charcoal py-2">{l.nameSnapshot ?? "—"}</td>
                <td className="text-ink py-2 font-mono">{l.sku ?? "—"}</td>
                <td className="text-ink py-2 text-right">{l.quantity ?? 1}</td>
                <td className="price text-charcoal py-2 text-right">
                  {formatCurrency(l.lineTotalPaise ?? 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <dl className="text-body-sm text-ink ml-auto mt-3 max-w-xs space-y-1">
          <div className="flex justify-between">
            <dt>Subtotal</dt>
            <dd>{formatCurrency(o.subtotalPaise ?? 0)}</dd>
          </div>
          {o.discountPaise ? (
            <div className="text-forest flex justify-between">
              <dt>Discount{o.couponCode ? ` (${o.couponCode})` : ""}</dt>
              <dd>− {formatCurrency(o.discountPaise)}</dd>
            </div>
          ) : null}
          {o.shippingPaise ? (
            <div className="flex justify-between">
              <dt>Shipping</dt>
              <dd>{formatCurrency(o.shippingPaise)}</dd>
            </div>
          ) : null}
          {o.codFeePaise ? (
            <div className="flex justify-between">
              <dt>COD fee</dt>
              <dd>{formatCurrency(o.codFeePaise)}</dd>
            </div>
          ) : null}
          {o.taxPaise ? (
            <div className="flex justify-between">
              <dt>GST</dt>
              <dd>{formatCurrency(o.taxPaise)}</dd>
            </div>
          ) : null}
          <div className="border-charcoal/10 text-heading-sm text-charcoal flex justify-between border-t pt-2">
            <dt>Total</dt>
            <dd className="price">{formatCurrency(o.grandTotalPaise ?? 0)}</dd>
          </div>
        </dl>
      </Card>

      {addr ? (
        <Card>
          <h2 className="font-display text-display-sm text-charcoal">Delivery address</h2>
          <p className="text-body-sm text-ink mt-2">
            {addr.name} · {addr.line1}
            {addr.line2 ? `, ${addr.line2}` : ""}, {addr.city}, {addr.state} {addr.pinCode} ·{" "}
            {addr.phone}
          </p>
          {o.customerNote ? (
            <p className="text-caption text-muted mt-2">Note: {o.customerNote}</p>
          ) : null}
        </Card>
      ) : null}

      <Card>
        <h2 className="font-display text-display-sm text-charcoal">Actions</h2>
        <p className="text-caption text-muted mt-1">
          Manual transitions are audit-logged as manual_override. The backend enforces the state
          machine — invalid transitions are rejected.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Select value={target} onChange={(e) => setTarget(e.target.value)} className="w-56">
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll("_", " ").toLowerCase()}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            onClick={() => transition.mutate(target)}
            disabled={transition.isPending}
          >
            {transition.isPending ? "Applying…" : "Apply transition"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending || o.status === "CANCELLED"}
          >
            Cancel order
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => approveReturn.mutate()}
            disabled={approveReturn.isPending || o.status !== "RETURN_REQUESTED"}
          >
            Approve return + refund
          </Button>
        </div>
      </Card>
    </div>
  );
}
